import React, { useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { BackSide } from 'three';
import type { Group, Mesh } from 'three';
import { BIN_HEX } from './colors';
import type { BinColor } from '../../data/schemas';
import {
  BIN_MAX_TILT,
  BIN_TRASH_SLOTS,
  DUMP_FALL_START,
  HOVER_HEIGHT,
  arcHeight,
  dumpPhase,
  trashPiece,
  type TrashPieceDef,
} from './dumpAnimation';

export type BinVisualState = 'ready' | 'tipped' | 'snowy' | 'emptied' | 'raising' | 'brushing';

/** Live state of the bin → truck dump while this bin is the one being emptied. */
export interface BinDumpState {
  /** 0..1 dump progress. */
  t: number;
  /** World offset from this bin's pad to the hover point over the hopper. */
  dx: number;
  dz: number;
  /** Unit direction (world XZ) from the bin toward the hopper. */
  dirX: number;
  dirZ: number;
}

export interface BinMeshProps {
  name: string;
  color: BinColor;
  position: [number, number, number];
  visual: BinVisualState;
  highlight?: boolean;
  surprise?: string | null;
  /** Set only for the bin currently flying to the truck. */
  dump?: BinDumpState | null;
  onTap: () => 'ok' | 'wrong' | 'progress';
}

/** One piece of rubbish (box / ball / cone) used in bins, the pile and falling pieces. */
export const TrashPiece: React.FC<{
  def: TrashPieceDef;
  position: [number, number, number];
  rotation?: [number, number, number];
}> = ({ def, position, rotation = [0, 0, 0] }) => (
  <mesh position={position} rotation={rotation} castShadow>
    {def.shape === 'box' ? <boxGeometry args={[def.size, def.size * 0.8, def.size]} /> : null}
    {def.shape === 'sphere' ? <sphereGeometry args={[def.size * 0.55, 10, 10]} /> : null}
    {def.shape === 'cone' ? <coneGeometry args={[def.size * 0.5, def.size * 1.1, 8]} /> : null}
    <meshStandardMaterial color={def.color} roughness={0.7} />
  </mesh>
);

/** Toy bin on a concrete pad with dark outline so green never blends into grass. */
export const BinMesh: React.FC<BinMeshProps> = ({
  name,
  color,
  position,
  visual,
  highlight = false,
  surprise = null,
  dump = null,
  onTap,
}) => {
  const [wobble, setWobble] = useState(0);
  const ring = useRef<Mesh>(null);
  const arrow = useRef<Mesh>(null);
  const flight = useRef<Group>(null);
  const tiltPivot = useRef<Group>(null);
  const dumpRef = useRef<BinDumpState | null>(dump);
  dumpRef.current = dump;
  const emptied = visual === 'emptied';
  const flying = dump != null && dump.t > 0;
  const hasTrash =
    !emptied &&
    (visual === 'ready' || visual === 'tipped' || visual === 'raising') &&
    !(dump != null && dump.t >= DUMP_FALL_START);
  const showLid = snowyLid(visual);
  const tipped = visual === 'tipped';
  const snowy = visual === 'snowy' || visual === 'brushing';
  const rotZ = tipped ? Math.PI / 2.4 : wobble;
  const scaleY = emptied ? 0.85 : 1;
  const lidColor = useMemo(() => BIN_HEX[color], [color]);
  const bodyShade = useMemo(() => {
    if (color === 'green') return '#2F9E44';
    if (color === 'blue') return '#1C6DD0';
    return '#D63B35';
  }, [color]);
  const outline = '#1A1A1A';

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    // Bin → hopper flight: arc over to the truck, tip, return.
    const d = dumpRef.current;
    if (flight.current && tiltPivot.current) {
      if (d && d.t > 0) {
        const ph = dumpPhase(d.t);
        flight.current.position.set(
          d.dx * ph.move,
          (HOVER_HEIGHT - 0.7) * ph.move + arcHeight(ph.move),
          d.dz * ph.move
        );
        flight.current.rotation.y = Math.atan2(-d.dirZ, d.dirX);
        tiltPivot.current.rotation.z = -ph.tilt * BIN_MAX_TILT;
      } else {
        flight.current.position.set(0, 0, 0);
        flight.current.rotation.y = 0;
        tiltPivot.current.rotation.z = 0;
      }
    }
    if (ring.current && highlight && !emptied && !flying) {
      // Brighter pulse: 1.0 → 1.15 over ~1s
      const s = 1.0 + (Math.sin(t * Math.PI * 2) * 0.5 + 0.5) * 0.15;
      ring.current.scale.set(s, 1, s);
      const mat = ring.current.material as { opacity?: number };
      if (mat.opacity != null) mat.opacity = 0.65 + Math.sin(t * Math.PI * 2) * 0.25;
    }
    if (arrow.current && highlight && !emptied && !flying) {
      arrow.current.position.y = 2.55 + Math.sin(t * 4) * 0.28;
    }
  });

  const handleTap = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    const result = onTap();
    if (result === 'wrong') {
      setWobble(0.28);
      window.setTimeout(() => setWobble(-0.22), 80);
      window.setTimeout(() => setWobble(0.12), 160);
      window.setTimeout(() => setWobble(0), 280);
    }
  };

  return (
    <group name={name} position={position}>
      {/* Grey concrete pad on the pavement — bins never sit on grass */}
      <mesh name={`${name}-pad`} position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[1.05, 20]} />
        <meshStandardMaterial color="#9CA3AF" roughness={0.9} />
      </mesh>
      <mesh name={`${name}-pad-edge`} position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.95, 1.08, 20]} />
        <meshStandardMaterial color="#6B7280" roughness={0.85} />
      </mesh>

      <group ref={flight}>
      <group ref={tiltPivot} position={[0, 0.7, 0]}>
      <group position={[0, -0.7, 0]}>
      <group rotation={[0, 0, rotZ]} scale={[1, scaleY, 1]}>
        {/* Hit area ≥1.5× visible bin, well above 96px on screen */}
        <mesh name={`${name}-hit`} visible={false} position={[0, 0.95, 0]} onPointerDown={handleTap}>
          <boxGeometry args={[2.8, 2.8, 2.8]} />
          <meshBasicMaterial transparent opacity={0} />
        </mesh>

        {highlight && !emptied && !flying ? (
          <mesh
            ref={ring}
            name={`${name}-pulse`}
            position={[0, 0.06, 0]}
            rotation={[-Math.PI / 2, 0, 0]}
          >
            <ringGeometry args={[0.7, 1.2, 28]} />
            <meshBasicMaterial color="#FFFFFF" transparent opacity={0.75} depthWrite={false} />
          </mesh>
        ) : null}

        {/* Inverted-hull outline: BackSide only so the shell never covers the coloured body */}
        <mesh name={`${name}-outline`} position={[0, 0.7, 0]} scale={[1.14, 1.12, 1.14]} renderOrder={0}>
          <boxGeometry args={[0.95, 1.15, 0.95]} />
          <meshBasicMaterial color={outline} side={BackSide} depthWrite={false} />
        </mesh>

        <mesh name={`${name}-body`} position={[0, 0.7, 0]} castShadow onPointerDown={handleTap} renderOrder={1}>
          <boxGeometry args={[0.95, 1.15, 0.95]} />
          <meshStandardMaterial
            color={bodyShade}
            emissive={highlight && !emptied ? lidColor : bodyShade}
            emissiveIntensity={highlight && !emptied ? 0.28 : 0.08}
            transparent={emptied}
            opacity={emptied ? 0.4 : 1}
            roughness={0.62}
            metalness={0.02}
          />
        </mesh>
        <mesh name={`${name}-rim-outline`} position={[0, 1.28, 0]} scale={[1.12, 1.5, 1.12]} renderOrder={0}>
          <boxGeometry args={[1.05, 0.12, 1.05]} />
          <meshBasicMaterial color={outline} side={BackSide} depthWrite={false} />
        </mesh>
        <mesh name={`${name}-rim`} position={[0, 1.28, 0]} castShadow renderOrder={1}>
          <boxGeometry args={[1.05, 0.12, 1.05]} />
          <meshStandardMaterial
            color={lidColor}
            roughness={0.5}
            metalness={0.02}
            emissive={lidColor}
            emissiveIntensity={0.06}
            transparent={emptied}
            opacity={emptied ? 0.4 : 1}
          />
        </mesh>

        {hasTrash ? (
          <group name={`${name}-trash`}>
            {BIN_TRASH_SLOTS.map((pos, i) => (
              <TrashPiece
                key={i}
                def={trashPiece(color, i)}
                position={pos}
                rotation={[0.3 * i, 0.9 * i, 0.25 * i]}
              />
            ))}
          </group>
        ) : null}

        {showLid && !emptied ? (
          <mesh name={`${name}-lid`} position={[0, 1.42, 0]} castShadow onPointerDown={handleTap} renderOrder={1}>
            <boxGeometry args={[1.0, 0.18, 1.0]} />
            <meshStandardMaterial
              color={lidColor}
              roughness={0.45}
              metalness={0.02}
              emissive={lidColor}
              emissiveIntensity={0.06}
            />
          </mesh>
        ) : null}
        {showLid && !emptied ? (
          <mesh name={`${name}-handle`} position={[0, 1.58, 0]}>
            <boxGeometry args={[0.35, 0.1, 0.12]} />
            <meshStandardMaterial color="#FFC94A" />
          </mesh>
        ) : null}

        {(
          [
            [0.35, 0.14, 0.42],
            [-0.35, 0.14, 0.42],
            [0.35, 0.14, -0.42],
            [-0.35, 0.14, -0.42],
          ] as [number, number, number][]
        ).map((p, i) => (
          <mesh key={i} name={`${name}-wheel-${i}`} position={p} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.12, 0.12, 0.1, 10]} />
            <meshStandardMaterial color="#374151" />
          </mesh>
        ))}

        {snowy && !emptied ? (
          <mesh name={`${name}-snow`} position={[0, 1.55, 0]}>
            <boxGeometry args={[1.05, 0.22, 1.05]} />
            <meshStandardMaterial color="#F5FBFF" />
          </mesh>
        ) : null}

        {highlight && !emptied && !flying ? (
          <mesh ref={arrow} name={`${name}-arrow`} position={[0, 2.55, 0]} rotation={[Math.PI, 0, 0]}>
            <coneGeometry args={[0.42, 0.75, 4]} />
            <meshStandardMaterial color="#FFE66D" emissive="#FFE66D" emissiveIntensity={0.7} />
          </mesh>
        ) : null}

        {surprise && emptied ? (
          <mesh name={`${name}-surprise-${surprise}`} position={[0, 1.65, 0]}>
            <sphereGeometry args={[0.3, 12, 12]} />
            <meshStandardMaterial color="#FFB6C1" />
          </mesh>
        ) : null}
      </group>
      </group>
      </group>
      </group>
    </group>
  );
};

/** Open, full bins show their rubbish; the lid is only drawn on snow-covered bins. */
function snowyLid(visual: BinVisualState): boolean {
  return visual === 'snowy' || visual === 'brushing';
}

export default BinMesh;
